import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Globe, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { memo, useMemo, useState } from "react";

const SourcesAvatars = memo(function SourcesAvatars({
  sources = [],
  onSourcesClick, // Keep for backwards compatibility but won't be used
  className,
}) {
  // Parse and extract sources data
  const parsedSources = useMemo(() => {
    if (!sources) {
      return [];
    }

    // Handle different data structures
    let sourceData = sources;

    // If sources is already an array, use it directly
    if (Array.isArray(sources)) {
      sourceData = sources;
    }
    // If sources is a nested object, extract the actual data
    else if (sources.result?.data) {
      sourceData = sources.result.data;
    } else if (sources.data) {
      sourceData = sources.data;
    } else if (sources.result?.result?.data) {
      sourceData = sources.result.result.data;
    }

    // For the new web search format, filter out the main content (first item with no URL)
    // and only keep the source citations
    if (Array.isArray(sourceData)) {
      sourceData = sourceData.filter((item) => item.url && item.url !== null);
    }

    // If sourceData is a string, parse it
    if (typeof sourceData === "string") {
      const results = [];
      const entries = sourceData
        .split("\n\n\n")
        .filter((entry) => entry.trim());

      for (const entry of entries) {
        const lines = entry.split("\n").filter((line) => line.trim());

        let url = "";
        let title = "";
        let description = "";

        for (const line of lines) {
          if (line.startsWith("URL: ")) {
            url = line.replace("URL: ", "").trim();
          } else if (line.startsWith("Title: ")) {
            title = line.replace("Title: ", "").trim();
          } else if (line.startsWith("Description: ")) {
            description = line.replace("Description: ", "").trim();
          }
        }

        if (url && title) {
          results.push({
            url,
            title,
            description,
            favicon: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`,
          });
        }
      }

      return results;
    }

    // If sourceData is already an array, return it
    if (Array.isArray(sourceData)) {
      const mappedSources = sourceData.map((source) => {
        // Use a more reliable favicon service with better fallback handling
        let faviconUrl = source.favicon;
        if (!faviconUrl && source.url) {
          try {
            const hostname = new URL(source.url).hostname;
            // Use Google's favicon service but we'll handle errors gracefully
            faviconUrl = `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
          } catch (e) {
            // Invalid URL, skip favicon
            faviconUrl = null;
          }
        }
        return {
          ...source,
          favicon: faviconUrl,
        };
      });

      return mappedSources;
    }

    // Check if sourceData is an object with numeric keys (like {0: {...}, 1: {...}, ...})
    if (
      sourceData &&
      typeof sourceData === "object" &&
      !Array.isArray(sourceData)
    ) {
      // Check if it has numeric keys
      const keys = Object.keys(sourceData);
      const isNumericKeys = keys.every((key) => !isNaN(parseInt(key)));

      if (isNumericKeys) {
        // Convert object with numeric keys to array
        const arrayData = [];
        for (let i = 0; i < keys.length; i++) {
          if (sourceData[i]) {
            arrayData.push(sourceData[i]);
          }
        }
        return arrayData
          .map((source) => {
            let faviconUrl = source.favicon;
            if (!faviconUrl && source.url) {
              try {
                const hostname = new URL(source.url).hostname;
                faviconUrl = `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
              } catch (e) {
                faviconUrl = null;
              }
            }
            return {
              ...source,
              favicon: faviconUrl,
            };
          })
          .filter((source) => source.url); // Filter out entries without URLs
      }
    }

    const finalSources = Array.isArray(sourceData) ? sourceData : [];

    return finalSources;
  }, [sources]);

  // Collapsible state - must be called before early return (Rules of Hooks)
  const [isOpen, setIsOpen] = useState(false);

  // Show first 10 sources with expandable "show more" (matching DeepResearchBlock)
  const [showAllSources, setShowAllSources] = useState(false);

  // Don't render if no sources (after hooks are called)
  if (!parsedSources || parsedSources.length === 0) {
    return null;
  }

  const visibleSources = showAllSources
    ? parsedSources
    : parsedSources.slice(0, 10);
  const hasMoreSources = parsedSources.length > 10;

  // Display avatars for trigger button (first 5)
  const displaySources = parsedSources.slice(0, 5);
  const remainingCount = Math.max(0, parsedSources.length - 5);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 rounded-full bg-transparent border-0 shadow-none hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors group"
          data-prevent-scroll="true"
        >
          <div className="flex items-center gap-1.5">
            <div className="flex items-center -space-x-1">
              {displaySources.map((source, index) => (
                <div
                  key={index}
                  className="relative w-4 h-4 rounded-full border border-background bg-muted flex items-center justify-center overflow-hidden"
                  style={{ zIndex: displaySources.length - index }}
                >
                  {source.favicon ? (
                    <img
                      src={source.favicon}
                      alt=""
                      className="w-3 h-3"
                      loading="lazy"
                      onError={(e) => {
                        // Hide the broken image and show the Globe fallback
                        if (e.target) {
                          e.target.style.display = "none";
                          // Prevent further error attempts by removing src immediately
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";
                          // Remove the error handler to prevent repeated calls
                          e.target.onerror = null;
                        }
                        const fallback = e.target?.nextSibling;
                        if (fallback) {
                          fallback.style.display = "flex";
                        }
                      }}
                      onLoad={(e) => {
                        // Ensure fallback is hidden when image loads successfully
                        const fallback = e.target?.nextSibling;
                        if (fallback) {
                          fallback.style.display = "none";
                        }
                      }}
                    />
                  ) : null}
                  <Globe
                    className="w-2.5 h-2.5 text-muted-foreground"
                    style={{ display: source.favicon ? "none" : "flex" }}
                  />
                </div>
              ))}
              {remainingCount > 0 && (
                <div className="relative w-4 h-4 rounded-full border border-background bg-muted flex items-center justify-center text-[8px] font-medium text-muted-foreground">
                  +{remainingCount}
                </div>
              )}
            </div>
            {isOpen ? (
              <ChevronUp className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden transition-all duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
        <div className="mt-2 space-y-2 rounded-lg border-2 border-border bg-sidebar p-3 shadow-sm">
          <div className="text-sm font-semibold text-foreground flex items-center gap-2 pb-1 border-b border-border">
            <span>Sources</span>
            <span className="text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
              {parsedSources.length}
            </span>
          </div>
          <div className="space-y-1.5 max-h-40 sm:max-h-48 overflow-y-auto rounded-md p-2 bg-background/50 border border-border/50 scrollbar-thin scrollbar-thumb-rounded">
            {visibleSources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline flex items-start gap-1.5 group min-h-[44px] sm:min-h-0 p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
              >
                <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                <span className="flex-1 break-words line-clamp-2">
                  {source.title || source.url}
                </span>
              </a>
            ))}
            {hasMoreSources && !showAllSources && (
              <button
                onClick={() => setShowAllSources(true)}
                className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline min-h-[44px] sm:min-h-0 flex items-center w-full justify-center py-2"
              >
                Show {parsedSources.length - 10} more sources
              </button>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

SourcesAvatars.displayName = "SourcesAvatars";

export default SourcesAvatars;
